import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useTranslation } from 'i18next-vue';
import { useToast } from 'primevue/usetoast';

import { useApiRequest } from '@peanuds/composables';
import { peanudsApi } from '@peanuds/shared';

import { toAppNotification, type MappableNovuNotification } from '../utils/notifications.mappers';
import {
  getNotificationDescription,
  getNotificationTitle,
  getNotificationToastSeverity,
} from '../utils/notificationPresentation';
import type { AppNotification, NotificationsTab } from '../types/notifications.types';
import { useNovuClient } from '../composables/useNovuClient';
import {
  createSyntheticNovuNotification,
  useNovuEvents,
  type SyntheticNotificationWorkflow,
} from '../composables/useNovuEvents';
import { useNotificationsFetchModule } from './modules/notifications-fetch.module';
import { useNotificationsReadModule } from './modules/notifications-read.module';

const SUCCESS_TOAST_LIFE_MS = 10_000;

export const useNotificationsStore = defineStore('notifications', () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { novu, isInitialized: isNovuReady } = useNovuClient();

  const tab = ref<NotificationsTab>('all');

  const notifications = ref<AppNotification[]>([]);
  const hasMore = ref(false);

  const unreadCount = ref(0);

  const { fetchNotifications, fetchUnreadCount, isLoadingNotifications, notificationsError } =
    useNotificationsFetchModule({
      novu,
      notifications,
      hasMore,
      unreadCount,
    });

  const {
    execute: identifySubscriber,
    isLoading: isIdentifyingSubscriber,
    error: identifySubscriberError,
  } = useApiRequest({
    request: (options: Parameters<typeof peanudsApi.identifyNovuSubscriber>[0]) =>
      peanudsApi.identifyNovuSubscriber(options),
  });

  const sortedNotifications = computed(() =>
    [...notifications.value].sort((a, b) => {
      const aTime = new Date(a.createdAt).getTime();
      const bTime = new Date(b.createdAt).getTime();
      if (!Number.isFinite(aTime) || !Number.isFinite(bTime)) return 0;
      return bTime - aTime;
    }),
  );

  const filteredNotifications = computed(() => {
    if (tab.value === 'all') return sortedNotifications.value;
    return sortedNotifications.value.filter((notification) =>
      notification.tags.includes(tab.value),
    );
  });

  const { markAsRead, markAllAsRead, isLocalReadInFlight } = useNotificationsReadModule({
    novu,
    notifications,
    unreadCount,
    fetchNotifications,
    fetchUnreadCount,
  });

  const setTab = (value: NotificationsTab) => {
    tab.value = value;
  };

  const handleNotificationReceived = (
    novuNotification: MappableNovuNotification,
  ): AppNotification | null => {
    const mapped = toAppNotification(novuNotification);

    if (!mapped) {
      console.warn('[NotificationsStore] ⚠️ Failed to map notification:', novuNotification);
      return null;
    }

    console.log('[NotificationsStore] ✅ Notification mapped & added to store:', mapped);
    notifications.value = [mapped, ...notifications.value];
    if (!mapped.read) {
      unreadCount.value += 1;
    }
    const severity = getNotificationToastSeverity(mapped);

    toast.add({
      severity,
      summary: getNotificationTitle(mapped, t),
      detail: getNotificationDescription(mapped),
      ...(severity === 'success' ? { life: SUCCESS_TOAST_LIFE_MS } : {}),
    });

    return mapped;
  };

  const simulateNotification = (workflow: SyntheticNotificationWorkflow): AppNotification | null =>
    handleNotificationReceived(createSyntheticNovuNotification(workflow));

  useNovuEvents({
    novu,
    isReady: isNovuReady,
    onReady: () => {
      console.debug('[NotificationsStore] 🚀 Novu client ready — loading initial data');
      void fetchNotifications();
      void fetchUnreadCount();
    },
    onNotificationReceived: handleNotificationReceived,
    onUnreadCountChanged: (total) => {
      if (isLocalReadInFlight.value) {
        console.debug(
          '[NotificationsStore] ⏭️ Skipping unread count update (local read in flight)',
        );
        return;
      }

      unreadCount.value = total;
      void fetchNotifications();
    },
  });

  return {
    // state
    tab,
    notifications,
    // derived
    unreadCount,
    filteredNotifications,
    sortedNotifications,
    hasMore,
    // flags
    isLoadingNotifications,
    notificationsError,
    isIdentifyingSubscriber,
    identifySubscriberError,
    // actions
    fetchNotifications,
    fetchUnreadCount,
    identifySubscriber,
    markAsRead,
    markAllAsRead,
    setTab,
    simulateNotification,
  };
});
